import type { Cookies } from 'cookie';
import { parse, serialize } from 'cookie';
import { jwtDecode } from 'jwt-decode';
import { useMemo } from 'react';

export interface JWTToken {
  aud: string;
  exp: number;
  iat: number;
  iss: string;
  monitoring_only: boolean;
  organization_id: number;
  role: string;
  session_id: number;
  user_id: number;
  user_type: 'cargo' | 'ship' | 'port_agent';
}

export function setToken(token: string): void {
  const parsedToken = jwtDecode<JWTToken>(token);
  document.cookie = serialize('token', token, {
    expires: new Date(parsedToken.exp * 1000),
    maxAge: parsedToken.exp * 1000 - new Date().getTime(),
    secure: process.env.APP_ENV !== 'development',
    sameSite: 'strict',
  });
}

function parseCookies(): Cookies | null {
  const cook = typeof window !== 'undefined' ? document.cookie : null;
  return cook ? parse(cook) : null;
}

export function getToken(): string | null {
  const cookies = parseCookies();
  return cookies?.token ?? null;
}

export function getParsedToken(): JWTToken | null {
  const token = getToken();
  return token ? jwtDecode(token) : null;
}

export function useParsedToken(): JWTToken | null {
  const token = getToken();

  return useMemo(() => {
    if (!token) return null;

    try {
      const parsedToken = jwtDecode<JWTToken>(token);
      // Check if token has expired before returning it
      if (Date.now() >= parsedToken.exp * 1000) {
        return null; // Return null for expired tokens
      }
      return parsedToken;
    } catch (_e) {
      return null;
    }
  }, [token]);
}

export function removeToken(): void {
  document.cookie = serialize('token', '', {
    maxAge: -1,
  });
}
